﻿import { useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { toast } from "sonner"
import { Loader2, /*Send*/ } from "lucide-react"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/modules/shared/ui/components/dialog"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/modules/shared/ui/components/form"
import { Input } from "@/modules/shared/ui/components/input"
import { Button } from "@/modules/shared/ui/components/button"
import { Spinner } from "@/modules/shared/ui/components/spinner"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/modules/shared/ui/components/select"
import {
  TAX_STATUS_OPTIONS,
  NATURE_OF_CONTRACT_OPTIONS,
  TITLE_TYPE_OPTIONS,
} from "@/modules/onboarding/constants/sageFormEnums"
import type { EmployeeDto } from "@/modules/onboarding/employee/dtos"
import type { EmployeeDetailsDto } from "@/modules/onboarding/employee/dtos"
import type {
  Sage300SubmissionDto,
  Sage300SubmitRequestDto,
  Sage300CompanyDetail,
  Sage300PayrunDefinition,
  Sage300LeaveCode,
} from "@/modules/profile/dtos/Sage300ConnectDto"
import { onboardingService } from "@/modules/onboarding/infrastructure/onboarding-service"
import { sage300ConnectorService } from "@/modules/profile/infrastructure/sage300ConnectorService"
import { selectCurrentOrganizationId } from "@/modules/organizations/store/organizationSlice"
import type { RootState } from "@/store"

export function PushToSageDialogWrapper({
  open,
  onOpenChange,
  employee,
  onSubmissionSuccess,
}: Props) {
  return (
    <PushToSageDialog
      open={open}
      onOpenChange={onOpenChange}
      employee={employee}
      onSubmissionSuccess={onSubmissionSuccess}
    />
  )
}

const schema = z.object({
  companyCode: z.string().min(1, "Required"),
  companyRuleCode: z.string().min(1, "Required"),
  employeeCode: z.string().min(1, "Required"),
  titleTypeCode: z
    .enum(["MR", "MISS", "MRS"] as const, { message: "Invalid title" })
    .optional(),
  leavePolicyCode: z.string().min(1, "Required"),
  remunerationEarnDefCode: z.string().min(1, "Required"),
  paymentRunDefCode: z.string().min(1, "Required"),
  taxStatusCode: z
    .enum(["DIRST", "ST", "TEMP", "INDCON"] as const, {
      message: "Invalid tax status",
    })
    .optional(),
  natureOfContractCode: z
    .enum(["PERM", "TEMP", "FIXED", "CASUAL"] as const, {
      message: "Invalid nature of contract",
    })
    .optional(),
})

type FormValues = z.infer<typeof schema>

// --- Helpers -----------------------------------------------------------------

function formatAddress(address: EmployeeDetailsDto["address"]): string {
  if (!address) return "—"
  if (typeof address === "string") return address
  const parts = [
    address.line1,
    address.line2,
    address.city,
    address.state,
    address.postalCode,
    address.country,
  ]
  return parts.filter(Boolean).join(", ") || "—"
}

function formatBankDetails(bank: EmployeeDetailsDto["bankDetails"]): string {
  if (!bank) return "—"
  if (typeof bank === "string") return bank
  const parts = [
    bank.bankName,
    bank.accountHolderName,
    bank.accountNumber,
    bank.branchCode,
  ]
  return parts.filter(Boolean).join(" · ") || "—"
}

function extractCountryFromAddress(
  address: EmployeeDetailsDto["address"],
): string | undefined {
  if (!address || typeof address === "string") return undefined
  return address.country || undefined
}

// --- Read-only row -----------------------------------------------------------

function InfoRow({ label, value }: { label: string; value?: string | null }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <span className="text-sm text-foreground font-medium">{value || "—"}</span>
    </div>
  )
}

function Field({
  form,
  name,
  label,
  required,
  placeholder,
  disabled = false,
}: {
  form: ReturnType<typeof useForm<FormValues>>
  name: keyof FormValues
  label: string
  required?: boolean
  placeholder?: string
  disabled?: boolean
}) {
  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => (
        <FormItem>
          <FormLabel className="text-muted-foreground">
            {label}
            {required && <span className="text-destructive ml-0.5">*</span>}
          </FormLabel>
          <FormControl>
            <Input
              {...field}
              value={field.value ?? ""}
              placeholder={placeholder}
              disabled={disabled}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  )
}

function SelectField({
  form,
  name,
  label,
  required,
  options,
  placeholder = "Select an option",
  disabled = false,
  onValueChange,
}: {
  form: ReturnType<typeof useForm<FormValues>>
  name: keyof FormValues
  label: string
  required?: boolean
  options: readonly { label: string; value: string }[]
  placeholder?: string
  disabled?: boolean
  onValueChange?: (value: string) => void
}) {
  return (
    <FormField
      control={form.control}
      name={name as Parameters<typeof form.control.register>[0]}
      render={({ field }) => (
        <FormItem>
          <FormLabel className="text-muted-foreground">
            {label}
            {required && <span className="text-destructive ml-0.5">*</span>}
          </FormLabel>
          <Select
            value={field.value ?? ""}
            onValueChange={(val) => {
              field.onChange(val)
              onValueChange?.(val)
            }}
            disabled={disabled}
          >
            <FormControl>
              <SelectTrigger className="bg-card text-foreground border-border focus-visible:border-primary focus-visible:ring-2 focus-visible:ring-primary/20 disabled:opacity-50 disabled:cursor-not-allowed">
                <SelectValue placeholder={placeholder} />
              </SelectTrigger>
            </FormControl>
            <SelectContent className="bg-card text-foreground border-border">
              {options.map((opt) => (
                <SelectItem
                  key={opt.value}
                  value={opt.value}
                >
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      )}
    />
  )
}

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  employee: EmployeeDto
  onSubmissionSuccess?: () => void
  onPushDocuments?: () => void
}

export function PushToSageDialog({
  open,
  onOpenChange,
  employee,
  onSubmissionSuccess,
  //onPushDocuments,
}: Props) {
  const organizationId = useSelector((s: RootState) =>
    selectCurrentOrganizationId(s),
  )
  const [isFetching, setIsFetching] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [employeeDetails, setEmployeeDetails] =
    useState<EmployeeDetailsDto | null>(null)
  const [savedSubmission, setSavedSubmission] =
    useState<Sage300SubmissionDto | null>(null)
  const [hasExistingSubmission, setHasExistingSubmission] = useState(false)
  const [companyDetails, setCompanyDetails] = useState<Sage300CompanyDetail[]>(
    [],
  )
  const [payrunDefinitions, setPayrunDefinitions] = useState<
    Sage300PayrunDefinition[]
  >([])
  const [leaveCodes, setLeaveCodes] = useState<Sage300LeaveCode[]>([])

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      companyCode: "",
      companyRuleCode: "",
      employeeCode: "",
      titleTypeCode: undefined,
      leavePolicyCode: "",
      remunerationEarnDefCode: "SALARY",
      paymentRunDefCode: "",
      taxStatusCode: undefined,
      natureOfContractCode: undefined,
    },
  })

  const watchedCompanyCode = form.watch("companyCode")

  const uniqueCompanyOptions = useMemo(() => {
    const seen = new Map<string, { label: string; value: string }>()
    for (const row of companyDetails) {
      if (!seen.has(row.companyCode)) {
        seen.set(row.companyCode, {
          label: row.companyName,
          value: row.companyCode,
        })
      }
    }
    return [...seen.values()]
  }, [companyDetails])

  const companyRuleOptions = useMemo(() => {
    if (!watchedCompanyCode) return []
    const seen = new Set<string>()
    const opts: { label: string; value: string }[] = []
    for (const row of companyDetails) {
      if (
        row.companyCode === watchedCompanyCode &&
        !seen.has(row.companyRuleCode)
      ) {
        seen.add(row.companyRuleCode)
        opts.push({ label: row.companyRuleCode, value: row.companyRuleCode })
      }
    }
    return opts
  }, [companyDetails, watchedCompanyCode])

  const payrunOptions = useMemo(() => {
    if (!watchedCompanyCode) return []
    return payrunDefinitions
      .filter((pd) => pd.companyCode === watchedCompanyCode)
      .map((pd) => ({ label: pd.code, value: pd.code }))
  }, [payrunDefinitions, watchedCompanyCode])

  const leaveCodeOptions = useMemo(() => {
    if (!watchedCompanyCode) return []
    return leaveCodes
      .filter((lc) => lc.companyCode === watchedCompanyCode)
      .map((lc) => ({ label: lc.code, value: lc.code }))
  }, [leaveCodes, watchedCompanyCode])

  useEffect(() => {
    if (!open || !organizationId) return

    setEmployeeDetails(null)
    setSavedSubmission(null)
    setHasExistingSubmission(false)
    setCompanyDetails([])
    setPayrunDefinitions([])
    setLeaveCodes([])
    setIsFetching(true)

    Promise.all([
      onboardingService.getEmployeeDetails(employee.id),
      sage300ConnectorService.getSubmission(organizationId, employee.id),
      sage300ConnectorService.getCompanyDetails(organizationId),
      sage300ConnectorService.getPayrunDefinitions(organizationId),
      sage300ConnectorService.getLeaveCodes(organizationId),
    ])
      .then(([details, submission, companies, payruns, leaveCodesResult]) => {
        setCompanyDetails(companies)
        setPayrunDefinitions(payruns)
        setLeaveCodes(leaveCodesResult)
        setEmployeeDetails(details)
        if (submission) {
          setSavedSubmission(submission)
          const isRealSubmission =
            !!submission.entityCode &&
            submission.status !== "FailedEmployeePush"
          setHasExistingSubmission(isRealSubmission)
          form.reset({
            companyCode: submission.companyCode || "",
            companyRuleCode: submission.companyRuleCode || "",
            employeeCode: submission.employeeCode || "",
            titleTypeCode:
              (submission.titleTypeCode as FormValues["titleTypeCode"]) ??
              undefined,
            leavePolicyCode: submission.leavePolicyCode || "",
            remunerationEarnDefCode:
              submission.remunerationEarnDefCode || "SALARY",
            paymentRunDefCode: submission.paymentRunDefCode || "",
            taxStatusCode:
              (submission.taxStatusCode as FormValues["taxStatusCode"]) ??
              undefined,
            natureOfContractCode:
              (submission.natureOfContractCode as FormValues["natureOfContractCode"]) ??
              undefined,
          })
        } else {
          form.reset({
            companyCode: "",
            companyRuleCode: "",
            employeeCode: "",
            titleTypeCode: undefined,
            leavePolicyCode: "",
            remunerationEarnDefCode: "SALARY",
            paymentRunDefCode: "",
            taxStatusCode: undefined,
            natureOfContractCode: undefined,
          })
        }
      })
      .catch(() => {
        toast.error("Failed to load form data")
      })
      .finally(() => {
        setIsFetching(false)
      })
  }, [open, employee.id, organizationId, form])

  const handleSubmit = async () => {
    const valid = await form.trigger()
    if (!valid) return

    if (!organizationId) {
      toast.error("No organisation selected")
      return
    }

    const values = form.getValues()
    const countryOfBirth = extractCountryFromAddress(employeeDetails?.address)

    // Typed against the request contract so a key that the API cannot bind is a compile
    // error rather than a value silently dropped on the wire.
    const payload: Sage300SubmitRequestDto = {
      companyCode: values.companyCode,
      companyRuleCode: values.companyRuleCode,
      employeeCode: values.employeeCode,
      leavePolicyCode: values.leavePolicyCode,
      paymentRunDefCode: values.paymentRunDefCode,
      remunerationEarnDefCode: values.remunerationEarnDefCode,
      ...(values.titleTypeCode ? { titleTypeCode: values.titleTypeCode } : {}),
      ...(countryOfBirth ? { countryOfBirth } : {}),
      ...(values.taxStatusCode ? { taxStatusCode: values.taxStatusCode } : {}),
      ...(values.natureOfContractCode
        ? { natureOfContractCode: values.natureOfContractCode }
        : {}),
    }

    setIsSubmitting(true)
    try {
      const result = await sage300ConnectorService.submitToSage300(
        organizationId,
        employee.id,
        payload,
      )

      if (result.success) {
        toast.success(
          result.entityCode
            ? `Submitted successfully – Sage 300 ref: ${result.entityCode}`
            : result.message,
        )
        onSubmissionSuccess?.()
        onOpenChange(false)
      } else {
        form.setError("root", { message: result.message })
      }
    } catch {
      toast.error("An unexpected error occurred while submitting to Sage 300")
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[80vh] overflow-y-auto bg-card"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Push to Sage 300</DialogTitle>
          <p className="text-sm text-muted-foreground mt-1">
            Review{" "}
            <span className="font-medium text-foreground">
              {employee.firstName} {employee.lastName}
            </span>
            {"'s details then fill in the required Sage 300 fields."}
          </p>
        </DialogHeader>

        {isFetching ? (
          <div className="flex items-center justify-center py-12">
            <Spinner className="size-6" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Read-only employee review */}
            <section>
              <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide mb-3">
                Employee Details (read-only)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4 p-4 border rounded-lg bg-muted">
                <InfoRow
                  label="First Name"
                  value={employeeDetails?.firstName}
                />
                <InfoRow label="Last Name" value={employeeDetails?.lastName} />
                <InfoRow label="Email" value={employeeDetails?.email} />
                <InfoRow label="Phone" value={employeeDetails?.phone} />
                <InfoRow
                  label="Date of Birth"
                  value={employeeDetails?.dateOfBirth}
                />
                <InfoRow label="Gender" value={employeeDetails?.gender} />
                <InfoRow
                  label="ID / Passport Number"
                  value={employeeDetails?.identityNumber}
                />
                <InfoRow
                  label="Tax Number"
                  value={employeeDetails?.taxNumber}
                />
                <InfoRow
                  label="Race Group (sent to Sage 300)"
                  value={employeeDetails?.raceGroup}
                />
                <InfoRow
                  label="Marital Status"
                  value={employeeDetails?.maritalStatus}
                />
                <InfoRow
                  label="Date Engaged"
                  value={employeeDetails?.employeeStartDate}
                />
                <InfoRow
                  label="Address"
                  value={formatAddress(employeeDetails?.address)}
                />
                <InfoRow
                  label="Bank Details"
                  value={formatBankDetails(employeeDetails?.bankDetails)}
                />
              </div>
            </section>

            {/* Sage 300 input fields */}
            <section>
              <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide mb-3">
                Sage 300 Information
              </h3>
              <Form {...form}>
                <form className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <SelectField
                    form={form}
                    name="companyCode"
                    label="Company"
                    required
                    options={uniqueCompanyOptions}
                    placeholder={
                      isFetching ? "Loading…" : "Select a company"
                    }
                    disabled={hasExistingSubmission || isFetching}
                    onValueChange={() => {
                      form.setValue("companyRuleCode", "")
                      form.setValue("paymentRunDefCode", "")
                      form.setValue("leavePolicyCode", "")
                    }}
                  />
                  <SelectField
                    form={form}
                    name="companyRuleCode"
                    label="Company Rule Code"
                    required
                    options={companyRuleOptions}
                    placeholder={
                      watchedCompanyCode
                        ? "Select a rule"
                        : "Select a company first"
                    }
                    disabled={
                      hasExistingSubmission ||
                      isFetching ||
                      !watchedCompanyCode
                    }
                  />
                  <Field
                    form={form}
                    name="employeeCode"
                    label="Employee Code"
                    required
                    disabled={hasExistingSubmission}
                  />
                  <SelectField
                    form={form}
                    name="titleTypeCode"
                    label="Title"
                    options={TITLE_TYPE_OPTIONS}
                    placeholder="Select title"
                    disabled={hasExistingSubmission}
                  />
                  <SelectField
                    form={form}
                    name="natureOfContractCode"
                    label="Nature of Contract"
                    required
                    options={NATURE_OF_CONTRACT_OPTIONS}
                    placeholder="Select nature of contract"
                    disabled={hasExistingSubmission}
                  />
                  <SelectField
                    form={form}
                    name="leavePolicyCode"
                    label="Leave Policy Code"
                    required
                    options={leaveCodeOptions}
                    placeholder={
                      watchedCompanyCode
                        ? "Select a leave code"
                        : "Select a company first"
                    }
                    disabled={
                      hasExistingSubmission ||
                      isFetching ||
                      !watchedCompanyCode
                    }
                  />
                  <Field
                    form={form}
                    name="remunerationEarnDefCode"
                    label="Earn Definition Code"
                    required
                    placeholder="e.g. SALARY"
                    disabled={hasExistingSubmission}
                  />
                  <SelectField
                    form={form}
                    name="paymentRunDefCode"
                    label="Payment Run Definition"
                    required
                    options={payrunOptions}
                    placeholder={
                      watchedCompanyCode
                        ? "Select a payrun definition"
                        : "Select a company first"
                    }
                    disabled={
                      hasExistingSubmission ||
                      isFetching ||
                      !watchedCompanyCode
                    }
                  />
                  <SelectField
                    form={form}
                    name="taxStatusCode"
                    label="Tax Status Code"
                    options={TAX_STATUS_OPTIONS}
                    placeholder="Select tax status"
                    disabled={hasExistingSubmission}
                  />
                </form>
              </Form>
            </section>

            {form.formState.errors.root && (
              <p className="text-sm text-destructive">
                {form.formState.errors.root.message}
              </p>
            )}

            {hasExistingSubmission && (
              <p className="text-sm text-primary bg-primary/5 p-3 rounded border border-primary/20">
                This employee has already been submitted to Sage 300 (Ref:{" "}
                {savedSubmission?.entityCode}). The form is displayed for
                reference only.
              </p>
            )}

            {!hasExistingSubmission &&
              savedSubmission?.status === "FailedEmployeePush" && (
                <div className="text-sm bg-destructive/5 border border-destructive/20 rounded p-3 space-y-1">
                  <p className="font-medium text-destructive">
                    Previous push to Sage 300 failed. Update the details below
                    and try again.
                  </p>
                  {savedSubmission.lastErrorPayload && (
                    <p className="text-destructive/80 break-words">
                      {savedSubmission.lastErrorPayload}
                    </p>
                  )}
                </div>
              )}
          </div>
        )}

        <DialogFooter className="pt-2">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          {/* Push Documents button hidden — pushing documents to Sage is disabled. */}
          {/* {hasExistingSubmission && onPushDocuments && (
            <Button
              variant="outline"
              onClick={() => {
                onOpenChange(false)
                onPushDocuments()
              }}
              disabled={isSubmitting}
            >
              <Send className="w-4 h-4 mr-2" />
              Push Documents
            </Button>
          )} */}
          <Button
            className="bg-primary hover:bg-primary/90 text-primary-foreground disabled:opacity-50"
            onClick={handleSubmit}
            disabled={isFetching || isSubmitting || hasExistingSubmission}
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Submitting…
              </>
            ) : (
              "Push to Sage"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
